/**
 * This application creates a widget for displaying an onboarding modal to
 * new users on VA.gov
 *
 * @module platform/site-wide/onboarding-modal
 */

import React from 'react';
import { Provider } from 'react-redux';

import startReactApp from 'platform/startup/react';
import { isLoggedIn, isLOA3 } from 'platform/user/selectors';
import { getClaims } from './actions/index';
import config from './config';
import { selectCanAccessClaims } from './selectors';
import './sass/onboarding-modal.scss';

/**
 * Sets up the Onboarding Modal widget with the given store
 *
 * @param {Redux.Store} store The common store used on the site
 */
export default async function startOnboardingModal(store) {
  const root = document.getElementById('onboarding-modal-root');
  if (!root) return;

  const onboardingModalFeatureFlag = config.featureFlag;

  // The user is already signed in at this point, so canAccessClaims is
  // immediately readable. Only the feature toggle may still be loading.
  // Start the claims fetch as soon as the toggle confirms the modal will show,
  // before React renders, so the modal may never need to show a loading spinner.
  const canAccess = selectCanAccessClaims(store.getState());
  if (canAccess) {
    let dispatched = false;
    const unsubscribe = store.subscribe(() => {
      if (dispatched) return;
      const state = store.getState();
      const { featureToggles, onboardingModal } = state;

      if (featureToggles?.loading) return;

      const featureEnabled =
        !onboardingModalFeatureFlag ||
        featureToggles?.[onboardingModalFeatureFlag];

      const userWantsOnboarding =
        state.user?.profile?.onboarding?.show === true;
      const isPasskeyLogin =
        state.user?.profile?.signIn?.serviceName === 'webauthn';
      const eligibleForModal =
        featureEnabled &&
        userWantsOnboarding &&
        !isPasskeyLogin &&
        isLoggedIn(state) &&
        isLOA3(state) &&
        config.paths.test(window.location.pathname);

      if (!eligibleForModal) {
        unsubscribe();
        return;
      }

      if (
        !onboardingModal?.claimsAvailability &&
        !onboardingModal?.claimsLoading
      ) {
        dispatched = true;
        unsubscribe();
        store.dispatch(getClaims());
      }
    });
  }

  const { default: OnboardingModal } = await import(
    /* webpackChunkName: "onboarding-modal-widget" */ './containers/OnboardingModal'
  );

  startReactApp(
    <Provider store={store}>
      <OnboardingModal />
    </Provider>,
    root,
  );
}
